import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedNumber } from 'react-intl';
import pluralize from 'pluralize';
import styled from 'styled-components';

import Title2 from '@joinforma/ui/components/Title2';
import SvgIcon from '@joinforma/ui/components/SvgIcon';
import { Color } from '@joinforma/ui/constants/themes';
import Calendar from 'assets/images/expiry-icon.png';
import Button from '@joinforma/ui/components/Button';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';
import { ReactComponent as CalendarSvg } from '@joinforma/ui/assets/icons/calendar.svg';

import ExpiringFundsCards from './ExpiringFundsCards';
import { StoreRoutes } from 'utils/routeGenerator';
import { logEvent } from 'utils/amplitude';
import { logRudderstackEvent } from 'utils/rudderstack';

import { EVENTS } from 'constants/events';
import { BannerType } from 'constants/products';
import { EXPIRING_FUNDS_EVENT_MAP_V2 } from 'constants/featureFlag';
import {
  ButtonSize,
  ButtonVariant,
  SvgIconSize,
} from '@joinforma/ui/constants/properties';

interface BannerContainerProps {
  banner_type: string;
  is_warning: boolean;
}

const Container = styled.div<BannerContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ is_warning, theme }) =>
    is_warning ? theme.colors.background.warning : Color.PINK_200};
  padding: 12px;
  width: 100%;
  ${({ banner_type }) =>
    banner_type === BannerType.ACCOUNT ||
    banner_type === BannerType.ACCOUNT_LIST
      ? 'border-radius: 8px'
      : ''};
  ${({ banner_type }) =>
    banner_type === BannerType.ACCOUNT ||
    banner_type === BannerType.ACCOUNT_LIST
      ? 'margin-bottom: 16px'
      : ''};

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    flex-direction: row;
    justify-content: ${({ banner_type }) =>
      banner_type === BannerType.STORE ? 'center' : 'space-between'};
    gap: 12px;
  }
`;

const ShopStore = styled(Button).attrs({
  variant: ButtonVariant.PRIMARY,
  size: ButtonSize.SMALL,
})`
  padding: 0px 16px;
  margin-top: 10px;
  width: 300px;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    margin-top: 0px;
    width: 118px;
  }
`;

const CalendarIcon = styled.img.attrs({
  src: Calendar,
  alt: 'Calendar',
})`
  display: none;

  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    display: inline;
    margin-right: 12px;
    width: 45px;
    height: 35px;
  }
`;
const CalendarControl = styled(SvgIcon).attrs({
  icon: CalendarSvg,
  size: SvgIconSize.MEDIUM,
})``;
const BannerInfoContainer = styled.div`
  display: flex;
  align-items: center;
`;
const MessageContainer = styled.div`
  width: 230px;
  @media (min-width: ${ScreenWidth.MOBILE_LANDSCAPE}) {
    width: 100%;
  }
`;

const Message = styled(Title2)``;

const FormattedLink = styled(Link)``;

const handleClick = (variation: string) => {
  const attributes = { variation: EXPIRING_FUNDS_EVENT_MAP_V2[variation] };
  logEvent(EVENTS.ExpiringFundsExperimentBannerClicked, attributes);
  logRudderstackEvent(EVENTS.ExpiringFundsExperimentBannerClicked, attributes);
};

const ExpiringFundsExperimentBanner = ({
  currency,
  days_left,
  total,
  variation,
  wallets_expiring,
  banner_type,
  wallet_id,
  expiring_funds_title_banner,
}: {
  currency: string;
  days_left: number;
  total: number;
  variation: string;
  wallets_expiring: Array<{
    id: string;
    wallet_type_id: string;
    amount: number;
    next_credit_date?: string;
    company_wallet_configuration: { wallet_name: string };
  }>;
  banner_type: string;
  wallet_id?: string;
  expiring_funds_title_banner?: boolean;
}) => {
  const multiple_accounts = wallets_expiring.length > 1 ?? false;
  const wallet_expiring = wallets_expiring?.find((ew) => ew.id === wallet_id);

  if (
    (wallets_expiring?.length === 2 || wallets_expiring?.length === 3) &&
    banner_type === BannerType.ACCOUNT_LIST &&
    expiring_funds_title_banner
  ) {
    return (
      <ExpiringFundsCards
        wallets_expiring={wallets_expiring}
        currency={currency}
        days_left={days_left}
        total={total}
        variation={variation}
      />
    );
  }

  const renderMessage = () =>
    multiple_accounts && banner_type !== BannerType.ACCOUNT ? (
      <MessageContainer>
        <Message>
          {`You’re about to lose `}
          <FormattedNumber
            value={Math.floor(total)}
            currency={currency}
            {...{ style: 'currency', maximumFractionDigits: 0 }}
          />{' '}
          {`in benefits. Use it before it disappears.`}
        </Message>
      </MessageContainer>
    ) : banner_type !== BannerType.ACCOUNT ? (
      <MessageContainer>
        <Message>
          {`You have `}
          <FormattedNumber
            value={wallets_expiring[0]?.amount as number}
            currency={currency}
            {...{ style: 'currency', maximumFractionDigits: 0 }}
          />{' '}
          {`in ${
            wallets_expiring[0]?.company_wallet_configuration.wallet_name
          } funds expiring in ${pluralize('day', days_left, true)}.`}
        </Message>
      </MessageContainer>
    ) : (
      <MessageContainer>
        <Message>
          {`You have `}
          <FormattedNumber
            value={total}
            currency={currency}
            {...{ style: 'currency', maximumFractionDigits: 0 }}
          />{' '}
          {`in ${
            wallet_expiring?.company_wallet_configuration.wallet_name
          } funds expiring in ${pluralize('day', days_left, true)}.`}
        </Message>
      </MessageContainer>
    );

  const is_warning =
    variation.startsWith('control') && banner_type === BannerType.STORE;

  return (
    <Container banner_type={banner_type} is_warning={is_warning}>
      {banner_type === BannerType.STORE ? (
        variation.startsWith('control') ? (
          <>
            <CalendarControl />
            <Message>
              {`You have funds expiring in ${pluralize(
                'day',
                days_left,
                true
              )}.`}
            </Message>
          </>
        ) : (
          <BannerInfoContainer>
            <CalendarIcon />
            <div>{renderMessage()}</div>
          </BannerInfoContainer>
        )
      ) : (
        <BannerInfoContainer>
          <CalendarIcon />
          <div>{renderMessage()}</div>
        </BannerInfoContainer>
      )}
      {banner_type === BannerType.STORE ? (
        variation.startsWith('control') ? (
          <FormattedLink
            to={StoreRoutes.collectionNoCost()}
            onClick={() => handleClick(variation)}
          >
            See what you can buy
          </FormattedLink>
        ) : (
          <FormattedLink
            to={
              variation.startsWith('control') || multiple_accounts
                ? StoreRoutes.collectionNoCost()
                : StoreRoutes.listFeatured({
                    account:
                      wallets_expiring[0]?.company_wallet_configuration
                        .wallet_name,
                    employee_wallet_id: wallets_expiring[0]?.id,
                  })
            }
            onClick={() => handleClick(variation)}
          >
            <ShopStore>Shop Store</ShopStore>
          </FormattedLink>
        )
      ) : (
        <FormattedLink
          to={
            variation.startsWith('control') ||
            (multiple_accounts && banner_type === BannerType.ACCOUNT_LIST)
              ? StoreRoutes.collectionNoCost()
              : banner_type === BannerType.ACCOUNT
              ? StoreRoutes.listFeatured({
                  account: wallet_expiring?.company_wallet_configuration
                    .wallet_name as string,
                  employee_wallet_id: wallet_expiring?.id as string,
                })
              : StoreRoutes.listFeatured({
                  account:
                    wallets_expiring[0]?.company_wallet_configuration
                      .wallet_name,
                  employee_wallet_id: wallets_expiring[0]?.id,
                })
          }
          onClick={() => handleClick(variation)}
        >
          <ShopStore>Shop Store</ShopStore>
        </FormattedLink>
      )}
    </Container>
  );
};

export default ExpiringFundsExperimentBanner;
